import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import { MoreVertical } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import { getBaseActivityType } from '../../utils/activities'

interface MetricsChartProps {
  workoutId: string
  vt1?: number // Ventilatory Threshold 1
  vt2?: number // Ventilatory Threshold 2
  maxHR?: number // Maximum HR for the athlete
  activityType?: string // Activity type (e.g., 'running', 'cycling')
  fields?: MetricType[] // Optional: specify which metrics to show (if not provided, uses default based on activity)
  defaultActiveMetrics?: MetricType[] // Optional: specify which metrics are active (visible) by default
}

interface MetricSample {
  timestamp_seconds: number
  value: number
}

interface MetricData {
  name: string
  label: string
  unit: string
  color: string
  samples: { timestamp: number; value: number | null }[]
}

type MetricType =
  | 'heart_rate'
  | 'speed'
  | 'cadence'
  | 'power'
  | 'altitude'
  | 'temperature'
  | 'skin_temperature'
  | 'heat_strain_index'

interface MetricConfig {
  id: string
  name: MetricType
  label: string
  unit: string
  color: string
  smoothingWindow: number
  downsampleInterval: number
}

interface MetricSettings {
  smoothingWindow: number
  downsampleInterval: number
}

interface ChartDataPoint {
  timestamp: number
  heart_rate?: number
  speed?: number
  cadence?: number
  power?: number
  altitude?: number
  temperature?: number
  skin_temperature?: number
  heat_strain_index?: number
}

const Container = styled.div`
  width: 100%;

  /* Remove blue outline on chart elements when clicked */
  .recharts-surface {
    outline: none;
  }

  .recharts-wrapper {
    outline: none;
  }
`

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.md} 0 0 0;
  justify-content: center;

  @media (max-width: 640px) {
    gap: ${theme.spacing.sm};
  }
`

const LegendItem = styled.div<{ $active: boolean; $color: string }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border: 2px solid ${({ theme, $active }) => ($active ? theme.colors.border : theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${({ theme, $active }) => ($active ? theme.colors.surfaceHover : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.colors.text : theme.colors.textSecondary)};
  font-size: ${theme.typography.sm};
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.primaryDark};
  }

  @media (max-width: 640px) {
    font-size: ${theme.typography.xs};
    padding: ${theme.spacing.xs};
  }
`

const LegendItemContent = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  background: none;
  border: none;
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  cursor: pointer;
  padding: 0;
  flex: 1;
`

const MenuButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 2px;
  border-radius: ${theme.borderRadius.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.default};
  position: relative;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  min-width: 220px;
  z-index: 1000;
  overflow: hidden;
  padding: ${theme.spacing.sm};
`

const DropdownSection = styled.div`
  margin-bottom: ${theme.spacing.sm};

  &:last-child {
    margin-bottom: 0;
  }
`

const DropdownLabel = styled.div`
  font-size: ${theme.typography.xs};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`

const DropdownControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`

const DropdownButton = styled.button<{ $active?: boolean }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  background: ${({ theme, $active }) => ($active ? theme.colors.surface : 'transparent')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  color: ${({ theme, $active }) => ($active ? theme.colors.text : theme.colors.textSecondary)};
  font-size: ${theme.typography.xs};
  cursor: pointer;
  transition: ${theme.transitions.default};
  flex: 1;

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    border-color: ${({ theme }) => theme.colors.primaryDark};
  }
`

const LegendColorDot = styled.span<{ $color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`

const LoadingMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${theme.typography.sm};
`

const ErrorMessage = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: ${theme.typography.sm};
`

const SkeletonWrapper = styled.div`
  .react-loading-skeleton {
    --base-color: ${({ theme }) => theme.colors.surface};
    --highlight-color: ${({ theme }) => theme.colors.border};
    border-radius: ${theme.borderRadius.md};
  }
`

const ChartSkeletonContainer = styled.div`
  width: 100%;
  height: 400px;
  padding: ${theme.spacing.md};
`

// Helper function to format time as mm:ss or hh:mm:ss
const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to convert speed (km/h) to pace (min/km)
const speedToPace = (speedKmh: number | null): number | null => {
  if (speedKmh === null) return null
  if (speedKmh === 0) return 0
  // pace in seconds per km = 3600 / speed
  // convert to minutes per km
  return 60 / speedKmh
}

// Helper function to convert speed (m/s) to swimming pace (min/100m)
const speedToSwimPace = (speedMs: number | null): number | null => {
  if (speedMs === null) return null
  if (speedMs === 0) return 0
  // pace in seconds per 100m = 100 / speed
  // convert to minutes per 100m
  return 100 / speedMs / 60
}

// Helper function to format pace as mm:ss/km
const formatPace = (paceMinPerKm: number): string => {
  if (paceMinPerKm === 0 || !isFinite(paceMinPerKm)) return '0:00'
  const mins = Math.floor(paceMinPerKm)
  const secs = Math.round((paceMinPerKm - mins) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to format swimming pace as mm:ss/100m
const formatSwimPace = (paceMinPer100m: number): string => {
  if (paceMinPer100m === 0 || !isFinite(paceMinPer100m)) return '0:00'
  const mins = Math.floor(paceMinPer100m)
  const secs = Math.round((paceMinPer100m - mins) * 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Helper function to determine appropriate tick interval based on duration
const getTickInterval = (durationSeconds: number): number => {
  // Convert to minutes for easier logic
  const durationMinutes = durationSeconds / 60

  if (durationMinutes <= 10) {
    return 60 // 1 minute intervals
  } else if (durationMinutes <= 60) {
    return 300 // 5 minute intervals
  } else if (durationMinutes <= 90) {
    return 900 // 15 minute intervals
  } else if (durationMinutes <= 180) {
    return 1800 // 30 minute intervals
  } else {
    return 3600 // 1 hour intervals
  }
}

// Helper function to generate tick values at regular intervals
const generateTicks = (maxTime: number, interval: number): number[] => {
  const ticks: number[] = [0]
  let currentTick = interval

  while (currentTick <= maxTime) {
    ticks.push(currentTick)
    currentTick += interval
  }

  // Always include the end time if it's not already there
  if (ticks[ticks.length - 1] !== maxTime && maxTime > 0) {
    ticks.push(maxTime)
  }

  return ticks
}

// Helper function to calculate appropriate Y-axis tick interval and domain
const calculateYAxisConfig = (
  min: number,
  max: number,
  metricName?: string,
  activityType?: string
): { domain: [number, number]; ticks: number[]; interval: number } => {
  const range = max - min

  // Determine appropriate tick interval based on range
  let interval: number
  if (range <= 12) {
    interval = 1
  } else if (range <= 20) {
    interval = 5
  } else if (range <= 50) {
    interval = 10
  } else if (range <= 100) {
    interval = 10
  } else if (range <= 200) {
    interval = 20
  } else if (range <= 500) {
    interval = 50
  } else {
    interval = 100
  }

  // Round domain to nearest interval
  const domainMin = Math.floor(min / interval) * interval
  const domainMax = Math.ceil(max / interval) * interval

  // Generate evenly spaced ticks
  const ticks: number[] = []
  for (let tick = domainMin; tick <= domainMax; tick += interval) {
    ticks.push(tick)
  }

  return {
    domain: [domainMin, domainMax],
    ticks,
    interval,
  }
}

const METRIC_CONFIGS: MetricConfig[] = [
  {
    id: 'altitude_5s',
    name: 'altitude',
    label: 'Altitude',
    unit: 'm',
    color: '#9CA3AF',
    smoothingWindow: 5,
    downsampleInterval: 5,
  },
  {
    id: 'heart_rate_1s',
    name: 'heart_rate',
    label: 'HR',
    unit: 'bpm',
    color: '#EF4444',
    smoothingWindow: 1,
    downsampleInterval: 1,
  },
  {
    id: 'power_5s',
    name: 'power',
    label: 'Power (5s avg)',
    unit: 'W',
    color: '#F59E0B',
    smoothingWindow: 5,
    downsampleInterval: 5,
  },
  {
    id: 'speed_30s',
    name: 'speed',
    label: 'Speed (30s avg)',
    unit: 'km/h',
    color: '#3B82F6',
    smoothingWindow: 30,
    downsampleInterval: 5,
  },
  {
    id: 'cadence_30s',
    name: 'cadence',
    label: 'Cadence (30s avg)',
    unit: 'rpm',
    color: '#10B981',
    smoothingWindow: 30,
    downsampleInterval: 5,
  },
  {
    id: 'temperature_60s',
    name: 'temperature',
    label: 'Temperature (60s avg)',
    unit: '°C',
    color: '#F59E0B',
    smoothingWindow: 60,
    downsampleInterval: 1,
  },
  {
    id: 'skin_temperature_60s',
    name: 'skin_temperature',
    label: 'Skin Temp (60s avg)',
    unit: '°C',
    color: '#EF4444',
    smoothingWindow: 60,
    downsampleInterval: 1,
  },
  {
    id: 'heat_strain_index_60s',
    name: 'heat_strain_index',
    label: 'Heat Strain Index',
    unit: '',
    color: '#7b16dc',
    smoothingWindow: 60,
    downsampleInterval: 1,
  },
]

// Custom tooltip component
const CustomTooltip = ({ active, payload, isRunning, isSwimming }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div
        style={{
          background: theme.colors.cardBackground,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borderRadius.sm,
          padding: theme.spacing.sm,
          fontSize: theme.typography.xs,
          color: theme.colors.text,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>Time: {formatTime(data.timestamp)}</div>
        {payload.map((entry: any, index: number) => {
          // Format pace specially for running and swimming activities
          const isRunningPace = isRunning && entry.dataKey === 'speed'
          const isSwimmingPace = isSwimming && entry.dataKey === 'speed'

          let formattedValue: string
          if (isRunningPace) {
            formattedValue = formatPace(entry.value)
          } else if (isSwimmingPace) {
            formattedValue = formatSwimPace(entry.value)
          } else {
            formattedValue = entry.value?.toFixed(1)
          }

          return (
            <div key={entry.name} style={{ color: entry.color }}>
              {entry.name}: {formattedValue} {entry.unit}
            </div>
          )
        })}
      </div>
    )
  }
  return null
}

export const MetricsChart: React.FC<MetricsChartProps> = ({
  workoutId,
  vt1,
  vt2,
  maxHR,
  activityType,
  fields,
  defaultActiveMetrics,
}) => {
  // Determine if this is a running or swimming activity
  const isRunning = getBaseActivityType(activityType ?? null) === 'running' || false
  const isSwimming = getBaseActivityType(activityType ?? null) === 'swimming' || false
  const isCycling = getBaseActivityType(activityType ?? null) === 'cycling' || false

  // Set default active metrics based on props or activity type
  const getDefaultActiveMetrics = () => {
    // If defaultActiveMetrics prop is specified, use that
    if (defaultActiveMetrics && defaultActiveMetrics.length > 0) {
      return new Set(defaultActiveMetrics)
    }

    // If fields are specified, use those
    if (fields && fields.length > 0) {
      return new Set(fields)
    }

    // Otherwise use defaults based on activity type
    const defaults = ['heart_rate']
    if (isRunning) {
      defaults.push('speed') // Pace for running
    } else if (isCycling) {
      defaults.push('power') // Power for cycling
    }
    return new Set(defaults)
  }

  const [metricsData, setMetricsData] = useState<MetricData[]>([])
  const [activeMetrics, setActiveMetrics] = useState<Set<string>>(getDefaultActiveMetrics())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openMenuMetric, setOpenMenuMetric] = useState<string | null>(null)
  const [metricSettings, setMetricSettings] = useState<Map<string, MetricSettings>>(new Map())
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpenMenuMetric(null)
      }
    }

    if (openMenuMetric) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuMetric])

  const getMetricSettings = useCallback(
    (metricName: string): MetricSettings => {
      const customSettings = metricSettings.get(metricName)
      if (customSettings) {
        return customSettings
      }

      // Return default settings from config
      const config = METRIC_CONFIGS.find((c) => c.name === metricName)
      return {
        smoothingWindow: config?.smoothingWindow || 1,
        downsampleInterval: config?.downsampleInterval || 1,
      }
    },
    [metricSettings]
  )

  const updateMetricSettings = (metricName: string, settings: Partial<MetricSettings>) => {
    setMetricSettings((prev) => {
      const newMap = new Map(prev)
      const currentSettings = getMetricSettings(metricName)

      // Auto-adjust sampling interval based on smoothing window
      const updatedSettings = { ...currentSettings, ...settings }
      if (settings.smoothingWindow !== undefined) {
        // Sampling interval: 1s for 1s smoothing, 5s for 5s and above
        updatedSettings.downsampleInterval = settings.smoothingWindow === 1 ? 1 : 5
      }

      newMap.set(metricName, updatedSettings)
      return newMap
    })
  }

  useEffect(() => {
    const loadAllMetrics = async () => {
      if (!workoutId) return

      try {
        setLoading(true)
        setError(null)

        // Filter configs based on fields prop if provided
        const configsToLoad =
          fields && fields.length > 0 ? METRIC_CONFIGS.filter((config) => fields.includes(config.name)) : METRIC_CONFIGS

        // Fetch all metrics in parallel
        const metricPromises = configsToLoad.map(async (config) => {
          const settings = getMetricSettings(config.name)

          const { data, error: rpcError } = await supabase.rpc('get_workout_metrics', {
            p_workout_id: workoutId,
            p_metric_name: config.name,
            p_downsample_interval: settings.downsampleInterval,
            p_smoothing_window: settings.smoothingWindow,
          })

          if (rpcError || !data || data.length === 0) {
            return null
          }

          const samples = (data as MetricSample[]).map((sample) => ({
            timestamp: sample.timestamp_seconds,
            value: sample.value,
          }))

          return {
            name: config.name,
            label: config.label,
            unit: config.unit,
            color: config.color,
            samples,
          } as MetricData
        })

        const results = await Promise.all(metricPromises)
        let availableMetrics = results.filter((m): m is MetricData => m !== null)

        // Update labels with current settings and convert data as needed
        availableMetrics = availableMetrics.map((metric) => {
          const settings = getMetricSettings(metric.name)
          const baseConfig = METRIC_CONFIGS.find((c) => c.name === metric.name)

          // Build dynamic label based on smoothing
          let dynamicLabel = baseConfig?.label.split(' (')[0] || metric.label
          if (settings.smoothingWindow > 1) {
            dynamicLabel = `${dynamicLabel} (${settings.smoothingWindow}s avg)`
          }

          // For running activities, convert speed to pace
          if (isRunning && metric.name === 'speed') {
            return {
              ...metric,
              label: `Pace (${settings.smoothingWindow}s avg)`,
              unit: 'min/km',
              samples: metric.samples.map((sample) => ({
                timestamp: sample.timestamp,
                // Filter out speeds slower than 9 km/h (likely walking/stopped) for better chart range
                value: sample.value && sample.value < 9 ? null : speedToPace(sample.value),
              })),
            }
          }

          // For swimming activities, convert speed to pace (min/100m) and rename cadence to stroke rate
          if (isSwimming) {
            if (metric.name === 'speed') {
              return {
                ...metric,
                label: `Pace (${settings.smoothingWindow}s avg)`,
                unit: 'min/100m',
                samples: metric.samples.map((sample) => ({
                  timestamp: sample.timestamp,
                  value: speedToSwimPace(sample.value),
                })),
              }
            }
            if (metric.name === 'cadence') {
              return {
                ...metric,
                label: `Stroke Rate (${settings.smoothingWindow}s avg)`,
                unit: 'spm',
              }
            }
          }

          // Return metric with updated label
          return {
            ...metric,
            label: dynamicLabel,
          }
        })

        setMetricsData(availableMetrics)
        console.log(`Loaded metrics: ${availableMetrics.map((m) => m.name).join(', ')}`)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metrics data')
        console.error('Error loading metrics data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadAllMetrics()
  }, [workoutId, isRunning, isSwimming, fields, metricSettings, getMetricSettings])

  const toggleMetric = (metricName: string) => {
    setActiveMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricName)) {
        newSet.delete(metricName)
      } else {
        newSet.add(metricName)
      }
      return newSet
    })
  }

  if (loading) {
    return (
      <Container>
        <ChartSkeletonContainer>
          <SkeletonWrapper>
            <Skeleton height={350} />
            <div style={{ marginTop: '16px', display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <Skeleton width={80} height={24} />
              <Skeleton width={80} height={24} />
              <Skeleton width={80} height={24} />
              <Skeleton width={80} height={24} />
            </div>
          </SkeletonWrapper>
        </ChartSkeletonContainer>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>Error: {error}</ErrorMessage>
      </Container>
    )
  }

  if (metricsData.length === 0) {
    return (
      <Container>
        <LoadingMessage>No metrics data available for this workout.</LoadingMessage>
      </Container>
    )
  }

  // Get active metrics data (excluding altitude which is rendered separately as background)
  const altitudeData = metricsData.find((m) => m.name === 'altitude')
  const activeMetricsData = metricsData.filter((m) => activeMetrics.has(m.name) && m.name !== 'altitude')
  // const temperatureData = metricsData.find((m) => m.name === 'temperature')

  // Merge all metric samples into a single array of data points keyed by timestamp
  const timestampMap = new Map<number, ChartDataPoint>()

  metricsData.forEach((metric) => {
    metric.samples.forEach((sample) => {
      if (!timestampMap.has(sample.timestamp)) {
        timestampMap.set(sample.timestamp, { timestamp: sample.timestamp })
      }
      const dataPoint = timestampMap.get(sample.timestamp)!
      dataPoint[metric.name as keyof ChartDataPoint] = sample.value as any
    })
  })

  // Convert map to sorted array
  const chartData = Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp)

  // Calculate domain and ticks for each metric
  const metricConfigs = new Map<string, { domain: [number, number]; ticks: number[] }>()

  activeMetricsData.forEach((metric) => {
    const values = metric.samples.map((s) => s.value).filter((v): v is number => v !== null && Number.isFinite(v))
    if (values.length > 0) {
      const nonNullValues: number[] = values.filter((v) => v !== null)
      const min = Math.min(...nonNullValues)
      const max = Math.max(...nonNullValues)
      // Add 10% padding to each side
      const padding = (max - min) * 0.1
      const paddedMin = min - padding
      const paddedMax = max + padding

      // Calculate evenly spaced ticks and adjusted domain
      const config = calculateYAxisConfig(paddedMin, paddedMax, metric.name, activityType)

      // For pace metrics (running and swimming), invert the y-axis so faster (lower values) appears at top
      const isPaceMetric = (isRunning || isSwimming) && metric.name === 'speed'

      metricConfigs.set(metric.name, {
        domain: isPaceMetric ? [config.domain[1], config.domain[0]] : config.domain,
        ticks: isPaceMetric ? [...config.ticks.reverse()] : config.ticks,
      })
    }
  })

  // Calculate domain and ticks for altitude (background metric)
  if (altitudeData) {
    const values = altitudeData.samples.map((s) => s.value).filter((v): v is number => v !== null && Number.isFinite(v))
    if (values.length > 0) {
      const nonNullValues: number[] = values.filter((v) => v !== null)
      const min = Math.min(...nonNullValues)
      const max = Math.max(...nonNullValues)
      // Add 10% padding to each side
      const padding = (max - min) * 0.1
      const paddedMin = min - padding
      const paddedMax = max + padding

      // Calculate evenly spaced ticks and adjusted domain
      const config = calculateYAxisConfig(paddedMin, paddedMax)
      metricConfigs.set('altitude', {
        domain: config.domain,
        ticks: config.ticks,
      })
    }
  }

  // Find max timestamp for X-axis
  const maxTime = chartData.length > 0 ? Math.max(...chartData.map((d) => d.timestamp)) : 0

  // Calculate appropriate tick interval and generate tick values
  const tickInterval = getTickInterval(maxTime)
  const xAxisTicks = generateTicks(maxTime, tickInterval)

  // Determine if we should show HR thresholds
  const showHRThresholds = activeMetrics.has('heart_rate') && (vt1 || vt2)

  // Calculate margins based on number of active metrics
  // Each Y-axis needs about 60px of space
  const numLeftAxes = Math.ceil(activeMetricsData.length / 2)
  const numRightAxes = Math.floor(activeMetricsData.length / 2)
  const leftMargin = numLeftAxes * 60
  const rightMargin = numRightAxes * 60 + 30

  return (
    <Container>
      <ResponsiveContainer width="100%" height={350}>
        <AreaChart data={chartData} margin={{ top: 20, right: rightMargin, left: leftMargin, bottom: 20 }}>
          {/* Define gradients for each metric */}
          <defs>
            {/* Altitude gradient (gray background) */}
            {altitudeData && (
              // biome-ignore lint/correctness/useUniqueElementIds: line is correct
              <linearGradient id="gradient-altitude" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9CA3AF" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#9CA3AF" stopOpacity={0.2} />
              </linearGradient>
            )}
            {/* Temperature gradient (blue to red)
            <linearGradient id="gradient-temperature" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#EF4444" stopOpacity={0.3} />
              <stop offset="25%" stopColor="#F59E0B" stopOpacity={0.3} />
              <stop offset="50%" stopColor="#10B981" stopOpacity={0.3} />
              <stop offset="75%" stopColor="#3B82F6" stopOpacity={0.3} />
              <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.3} />
            </linearGradient>
            */}
            {/* Other metric gradients */}
            {activeMetricsData.map((metric) => {
              // Skip temperature as it has custom gradient
              // if (metric.name === 'temperature') return null
              return (
                <linearGradient
                  key={`gradient-${metric.name}`}
                  id={`gradient-${metric.name}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={metric.color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={metric.color} stopOpacity={0} />
                </linearGradient>
              )
            })}
          </defs>

          <CartesianGrid stroke="#E5E7EB" />
          <XAxis
            dataKey="timestamp"
            stroke={theme.colors.textSecondary}
            tick={{ fill: theme.colors.textSecondary, fontSize: 10 }}
            label={{ value: 'Time', position: 'insideBottom', offset: -10, fill: theme.colors.text }}
            type="number"
            domain={[0, maxTime]}
            ticks={xAxisTicks}
            tickFormatter={(value) => formatTime(value)}
          />

          {/* Altitude Y-axis (rendered on right side, hidden) */}
          {altitudeData && (
            <YAxis
              yAxisId="altitude"
              orientation="right"
              hide={true}
              domain={metricConfigs.get('altitude')?.domain || ['auto', 'auto']}
            />
          )}

          {/* Render a Y-axis for each active metric */}
          {activeMetricsData.map((metric, index) => {
            const config = metricConfigs.get(metric.name)
            const orientation = index % 2 === 0 ? 'left' : 'right'

            return (
              <YAxis
                key={metric.name}
                yAxisId={metric.name}
                orientation={orientation}
                stroke={metric.color}
                tick={{ fill: metric.color, fontSize: 10 }}
                domain={config ? config.domain : ['auto', 'auto']}
                ticks={config?.ticks}
                tickFormatter={(value) => {
                  // Format pace values for running and swimming activities with speed metric
                  if (isRunning && metric.name === 'speed') {
                    return formatPace(value)
                  }
                  if (isSwimming && metric.name === 'speed') {
                    return formatSwimPace(value)
                  }
                  return value.toFixed(0)
                }}
                reversed={isRunning && metric.name === 'speed'}
                label={{
                  value: `${metric.label}`,
                  angle: orientation === 'left' ? -90 : 90,
                  position: orientation === 'left' ? 'insideLeft' : 'insideRight',
                  fill: metric.color,
                  fontSize: 11,
                  offset: 10,
                }}
              />
            )
          })}

          <Tooltip content={<CustomTooltip isRunning={isRunning} isSwimming={isSwimming} />} />

          {/* Render altitude area in background (gray) */}
          {altitudeData && (
            <Area
              type="monotone"
              dataKey="altitude"
              stroke="#9CA3AF"
              strokeWidth={0}
              fill="url(#gradient-altitude)"
              dot={false}
              name="Altitude"
              unit="m"
              yAxisId="altitude"
              connectNulls={true}
            />
          )}

          {/* Render temperature area in background (blue to red)
          {temperatureData && (
            <Area
              type="monotone"
              dataKey="temperature"
              stroke="#9CA3AF"
              strokeWidth={0}
              fill="url(#gradient-temperature)"
              dot={false}
              name="Temperature"
              unit="m"
              yAxisId="temperature"
              connectNulls
            />
          )}
           */}

          {/* Render areas for each active metric */}
          {activeMetricsData.map((metric) => {
            return (
              <Area
                key={metric.name}
                type="monotone"
                dataKey={metric.name}
                baseValue={isRunning && metric.name === 'speed' ? 10 : undefined}
                stroke={metric.color}
                strokeWidth={2}
                fill={`url(#gradient-${metric.name})`}
                dot={false}
                name={metric.label}
                unit={metric.unit}
                yAxisId={metric.name}
                connectNulls={true}
              />
            )
          })}

          {/* HR Threshold Reference Lines */}
          {showHRThresholds && vt1 && (
            <ReferenceLine
              y={vt1}
              yAxisId="heart_rate"
              stroke="#F59E0B"
              strokeWidth={2}
              opacity={0.25}
              strokeDasharray="5 5"
              label={{
                value: `VT1 (${vt1})`,
                position: 'right',
                fill: '#F59E0B',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
          {showHRThresholds && vt2 && (
            <ReferenceLine
              y={vt2}
              yAxisId="heart_rate"
              stroke="#EF4444"
              strokeWidth={2}
              opacity={0.25}
              strokeDasharray="5 5"
              label={{
                value: `VT2 (${vt2})`,
                position: 'right',
                fill: '#EF4444',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
          {showHRThresholds && maxHR && (
            <ReferenceLine
              y={maxHR}
              yAxisId="heart_rate"
              stroke="#DC2626"
              strokeWidth={2}
              opacity={0.25}
              strokeDasharray="5 5"
              label={{
                value: `Max HR (${maxHR})`,
                position: 'right',
                fill: '#DC2626',
                fontSize: 11,
                fontWeight: 600,
              }}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>

      {/* Legend */}
      <LegendContainer>
        {metricsData
          .filter((metric) => metric.name !== 'altitude')
          .map((metric) => {
            const isActive = activeMetrics.has(metric.name)
            const settings = getMetricSettings(metric.name)
            const isMenuOpen = openMenuMetric === metric.name

            // Define available smoothing options
            const smoothingOptions = [
              { label: '1s', value: 1 },
              { label: '5s', value: 5 },
              { label: '10s', value: 10 },
              { label: '30s', value: 30 },
              { label: '1min', value: 60 },
              { label: '5min', value: 5 * 60 },
              { label: '10min', value: 10 * 60 },
              { label: '30min', value: 30 * 60 },
              { label: '1h', value: 60 * 60 },
            ]

            return (
              <LegendItem key={metric.name} $active={isActive} $color={metric.color} ref={isMenuOpen ? menuRef : null}>
                <LegendItemContent onClick={() => toggleMetric(metric.name)}>
                  <LegendColorDot $color={metric.color} />
                  {metric.label} {metric.unit ? `[${metric.unit}]` : ''}
                </LegendItemContent>
                <MenuButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenMenuMetric(isMenuOpen ? null : metric.name)
                  }}
                  title="Customize metric"
                >
                  <MoreVertical />
                </MenuButton>

                {isMenuOpen && (
                  <DropdownMenu onClick={(e) => e.stopPropagation()}>
                    <DropdownSection>
                      <DropdownLabel>Smoothing</DropdownLabel>
                      <DropdownControl>
                        {smoothingOptions.map((option) => (
                          <DropdownButton
                            key={option.value}
                            $active={settings.smoothingWindow === option.value}
                            onClick={() => {
                              updateMetricSettings(metric.name, { smoothingWindow: option.value })
                            }}
                          >
                            {option.label}
                          </DropdownButton>
                        ))}
                      </DropdownControl>
                    </DropdownSection>
                  </DropdownMenu>
                )}
              </LegendItem>
            )
          })}
      </LegendContainer>
    </Container>
  )
}
